import React, { useEffect, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { LinkContainer } from 'react-router-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import { Nav, Navbar, NavDropdown, Form } from 'react-bootstrap'
import { getUserDetails, logout } from '../actions/userActions'
import { logoutOwner } from '../actions/ownerActions'
import { getSettings } from '../actions/settingActions'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCashRegister } from '@fortawesome/free-solid-svg-icons' // Example icon
import { ThemeContext } from './ThemeContext.js'

const Header = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { theme, isDarkMode, setIsDarkMode } = useContext(ThemeContext)

  const navTextStyle = {
    color: theme.text
  }

  const dropdownItemStyle = {
    color: theme.text
  }

  const dropdownTitle = (label) => <span style={navTextStyle}>{label}</span>

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const ownerLogin = useSelector((state) => state.ownerLogin)
  const { ownerInfo } = ownerLogin

  const isAuthenticated = Boolean(userInfo || ownerInfo)
  const canManageAll = Boolean(ownerInfo || (userInfo && userInfo.isAdmin))

  const userDetails = useSelector((state) => state.userDetails)
  const { user } = userDetails

  const settingList = useSelector((state) => state.settingList)
  const { settings, success } = settingList

  const activeDisplayName = ownerInfo
    ? ownerInfo.name || 'Owner'
    : userInfo?.name || user?.name || 'User'

  const logoutHandler = async () => {
    if (userInfo) {
      const id = userInfo._id
      localStorage.removeItem('userInfo')
      localStorage.removeItem('qrcodeScan')
      dispatch(logout(id))
    } else if (ownerInfo) {
      await dispatch(logoutOwner(ownerInfo._id))
      navigate('/owner/login')
    }
  }

  useEffect(() => {
    if (isAuthenticated && !success) {
      dispatch(getSettings())
    } else if (userInfo && success) {
      dispatch(getUserDetails(userInfo._id))
    }
  }, [isAuthenticated, userInfo, success, dispatch])

  return (
    <>
      <Navbar
        className='themed-header px-4 py-3 border-bottom shadow-sm'
        variant={isDarkMode ? 'dark' : 'light'}
        style={{
          '--header-text': theme.text,
          '--header-subtext': theme.subtext,
          '--header-card': theme.card,
          '--header-border': isDarkMode ? '#2D342D' : '#DFE6E9',
          '--header-hover-bg': isDarkMode ? '#2F3A33' : '#EDF3EE',
          '--header-active-bg': isDarkMode ? '#3A4A40' : '#E1EAE3',
          '--header-top-hover-bg': isDarkMode
            ? 'rgba(223, 255, 0, 0.12)'
            : 'rgba(110, 127, 107, 0.14)',
          '--header-top-active-bg': isDarkMode
            ? 'rgba(223, 255, 0, 0.2)'
            : 'rgba(110, 127, 107, 0.22)',
          '--header-focus-ring': isDarkMode
            ? 'rgba(223, 255, 0, 0.3)'
            : 'rgba(110, 127, 107, 0.35)',
          backgroundColor: theme.card,
          borderColor: isDarkMode ? '#040404' : '#DFE6E9'
        }}
        expand='lg'
      >
        {!isAuthenticated ? (
          <Navbar.Brand
            as={Link}
            to='/'
            className='fw-bold'
            style={{ color: theme.text }}
          >
            <span style={{ color: theme.accent }}>🌿</span> Geranium PoS
          </Navbar.Brand>
        ) : (
          <Navbar.Brand className='fw-bold' style={{ color: theme.text }}>
            {settings.taxrate !== undefined && (
              <span>
                <FontAwesomeIcon icon={faCashRegister} />{' '}
                {` Tax ${settings.taxrate}% `}
              </span>
            )}
            {settings.currency}{' '}
            {ownerInfo
              ? `Owner: ${activeDisplayName}`
              : `User: ${activeDisplayName}`}
          </Navbar.Brand>
        )}

        <Navbar.Toggle aria-controls='basic-navbar-nav' />
        <Navbar.Collapse id='basic-navbar-nav'>
          <Nav className='ms-auto d-flex align-items-center gap-3'>
            <Form.Check
              type='switch'
              id='theme-switch'
              label={isDarkMode ? '🌙 Dark' : '☀️ Light'}
              checked={isDarkMode}
              onChange={() => setIsDarkMode(!isDarkMode)}
              className='small'
              style={{ color: theme.subtext }}
            />

            {!isAuthenticated && (
              <NavDropdown
                title={<span style={{ color: theme.text }}>Menu</span>}
                id='basic-nav-dropdown'
                className='themed-nav-dropdown'
                menuVariant={isDarkMode ? 'dark' : undefined}
              >
                <LinkContainer to='/login'>
                  <Nav.Link
                    className='themed-dropdown-link'
                    style={dropdownItemStyle}
                  >
                    <i className='fas fa-user'></i> User Login
                  </Nav.Link>
                </LinkContainer>
                {/* <LinkContainer to='/register'>
                  <Nav.Link
                    className='themed-dropdown-link'
                    style={dropdownItemStyle}
                  >
                    <i className='fas fa-user-shield'></i> User Register
                  </Nav.Link>
                </LinkContainer> */}
                <LinkContainer to='/owner/login'>
                  <Nav.Link
                    className='themed-dropdown-link'
                    style={dropdownItemStyle}
                  >
                    <i className='fas fa-user-cog'></i> Owner Login
                  </Nav.Link>
                </LinkContainer>
                <LinkContainer to='/owner/register'>
                  <Nav.Link
                    className='themed-dropdown-link'
                    style={dropdownItemStyle}
                  >
                    <i className='fas fa-user-lock'></i> Owner Register
                  </Nav.Link>
                </LinkContainer>
              </NavDropdown>
            )}
            {isAuthenticated && (
              <>
                <NavDropdown
                  title={dropdownTitle('Sales')}
                  id='sales-dropdown'
                  className='themed-nav-dropdown'
                  menuVariant={isDarkMode ? 'dark' : undefined}
                >
                  <LinkContainer to='/pos'>
                    <NavDropdown.Item
                      className='themed-dropdown-item'
                      style={dropdownItemStyle}
                    >
                      Sales Terminal
                    </NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/returns'>
                    <NavDropdown.Item
                      className='themed-dropdown-item'
                      style={dropdownItemStyle}
                    >
                      Sales Return
                    </NavDropdown.Item>
                  </LinkContainer>
                </NavDropdown>

                {canManageAll && (
                  <NavDropdown
                    title={dropdownTitle('Inventory')}
                    className='themed-nav-dropdown'
                    menuVariant={isDarkMode ? 'dark' : undefined}
                  >
                    <LinkContainer to='/inventory'>
                      <NavDropdown.Item
                        className='themed-dropdown-item'
                        style={dropdownItemStyle}
                      >
                        List Inventory
                      </NavDropdown.Item>
                    </LinkContainer>

                    <LinkContainer to='/inventory/add'>
                      <NavDropdown.Item
                        className='themed-dropdown-item'
                        style={dropdownItemStyle}
                      >
                        Add Entry
                      </NavDropdown.Item>
                    </LinkContainer>

                    <LinkContainer to='/items'>
                      <NavDropdown.Item
                        className='themed-dropdown-item'
                        style={dropdownItemStyle}
                      >
                        List Items
                      </NavDropdown.Item>
                    </LinkContainer>

                    <LinkContainer to='/name'>
                      <NavDropdown.Item
                        className='themed-dropdown-item'
                        style={dropdownItemStyle}
                      >
                        List Manufacturers
                      </NavDropdown.Item>
                    </LinkContainer>
                  </NavDropdown>
                )}
                {canManageAll && (
                  <NavDropdown
                    title={dropdownTitle('Admin')}
                    className='themed-nav-dropdown'
                    menuVariant={isDarkMode ? 'dark' : undefined}
                  >
                    <LinkContainer to='/admin/userlist'>
                      <NavDropdown.Item
                        className='themed-dropdown-item'
                        style={dropdownItemStyle}
                      >
                        Users
                      </NavDropdown.Item>
                    </LinkContainer>
                  </NavDropdown>
                )}
                <NavDropdown
                  title={dropdownTitle('Menu')}
                  className='themed-nav-dropdown'
                  menuVariant={isDarkMode ? 'dark' : undefined}
                >
                  {canManageAll && (
                    <LinkContainer to='/dashboard'>
                      <NavDropdown.Item
                        className='themed-dropdown-item'
                        style={dropdownItemStyle}
                      >
                        Dashboard
                      </NavDropdown.Item>
                    </LinkContainer>
                  )}
                  {canManageAll && (
                    <LinkContainer to='/reports'>
                      <NavDropdown.Item
                        className='themed-dropdown-item'
                        style={dropdownItemStyle}
                      >
                        Reports
                      </NavDropdown.Item>
                    </LinkContainer>
                  )}
                  {canManageAll && (
                    <LinkContainer to='/settings'>
                      <NavDropdown.Item
                        className='themed-dropdown-item'
                        style={dropdownItemStyle}
                      >
                        Settings
                      </NavDropdown.Item>
                    </LinkContainer>
                  )}
                  {userInfo && (
                    <LinkContainer to='/profile'>
                      <NavDropdown.Item
                        className='themed-dropdown-item'
                        style={dropdownItemStyle}
                      >
                        Profile
                      </NavDropdown.Item>
                    </LinkContainer>
                  )}
                  {ownerInfo && (
                    <LinkContainer to='/owner/profile'>
                      <NavDropdown.Item
                        className='themed-dropdown-item'
                        style={dropdownItemStyle}
                      >
                        Profile
                      </NavDropdown.Item>
                    </LinkContainer>
                  )}
                  <NavDropdown.Item
                    className='themed-dropdown-item'
                    as='a'
                    href='mailto:info@hermanosgazpachos.ca'
                    style={dropdownItemStyle}
                  >
                    Contact Us
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item
                    className='themed-dropdown-item'
                    onClick={logoutHandler}
                    style={dropdownItemStyle}
                  >
                    Logout
                  </NavDropdown.Item>
                </NavDropdown>
              </>
            )}
          </Nav>
        </Navbar.Collapse>
      </Navbar>
    </>
  )
}

export default Header
